import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Brand } from "@/pages/Login";
import { api, errorText } from "@/lib/api";
import { Field } from "@/components/onboarding/Field";

const POINTS = [
  "Your own sign-up page with your logo, colours and name, with no mention of us",
  "Properties pay for the module and subscription directly when they sign up",
  "You help with the install and mark it complete from your dashboard",
];

export default function MspSignup() {
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/signup/msp", form);
      window.location.href = "/app";
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr] bg-[#07090E]">
      <section className="hidden lg:flex flex-col justify-between p-14 grid-bg border-r border-[#222D3D]">
        <Link to="/"><Brand /></Link>
        <div className="fade-up max-w-lg">
          <p className="font-mono text-xs text-emerald-400 tracking-[0.3em] uppercase mb-6">MSP partner program</p>
          <h1 className="text-5xl font-semibold leading-[1.05] text-slate-100">Offer AI agent monitoring under your own name.</h1>
          <ul className="mt-8 space-y-3 text-slate-400 text-sm">{POINTS.map((p) => <li key={p} className="border-l-2 border-emerald-500 pl-3">{p}</li>)}</ul>
        </div>
        <p className="font-mono text-[11px] text-slate-600">white-label · partner portal</p>
      </section>
      <section className="flex items-center justify-center p-6">
        <form onSubmit={submit} className="w-full max-w-sm space-y-5 fade-up" data-testid="msp-signup-form">
          <div className="flex items-center justify-between gap-4">
            <Link to="/" className="lg:hidden" data-testid="msp-brand-home-link"><Brand /></Link>
            <Link to="/" className="ml-auto flex items-center gap-1 text-sm text-slate-400 hover:text-slate-100 transition-colors shrink-0" data-testid="msp-back-home-link"><ArrowLeft className="h-4 w-4" />Home</Link>
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-slate-100">Become an MSP partner</h2>
            <p className="text-sm text-slate-500 mt-1">We review each application. Your sign-up page goes live once we approve you. <Link to="/partners" className="text-emerald-400" data-testid="msp-program-link">How the program works</Link></p>
          </div>
          <Field id="msp-company" label="Company name" required value={form.company} onChange={set("company")} />
          <div className="grid grid-cols-2 gap-3">
            <Field id="msp-name" label="Your name" required value={form.name} onChange={set("name")} />
            <Field id="msp-phone" label="Phone (optional)" value={form.phone} onChange={set("phone")} />
          </div>
          <Field id="msp-email" label="Work email" type="email" required value={form.email} onChange={set("email")} />
          <Field id="msp-password" label="Password" type="password" required minLength={8} value={form.password} onChange={set("password")} hint="At least 8 characters." />
          {error && <p className="text-sm text-rose-400" data-testid="msp-signup-error">{error}</p>}
          <Button type="submit" disabled={busy} className="w-full bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold" data-testid="msp-signup-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Apply as an MSP"}
          </Button>
          <p className="text-sm text-slate-500 text-center">Already a partner? <Link to="/login" className="text-emerald-400" data-testid="msp-signin-link">Sign in</Link></p>
        </form>
      </section>
    </div>
  );
}
